import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { MapPin, Search, Truck, PackageCheck, Clock } from "lucide-react";

export default function TrackShipment() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialId = searchParams.get("id") || "";
  const [id, setId] = useState(initialId);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const runSearch = async (searchId) => {
    if (!searchId) return;
    setErr(""); setLoading(true); setData(null);
    try {
      const { data: d } = await api.get(`/tracking/${encodeURIComponent(searchId)}`);
      setData(d);
    } catch { setErr("Shipment not found. Please check your tracking number and try again."); }
    finally { setLoading(false); }
  };

  const search = (e) => {
    e.preventDefault();
    const q = id.trim();
    if (!q) return;
    setSearchParams({ id: q });
    runSearch(q);
  };

  // Auto-run on landing via ?id=
  useEffect(() => {
    if (initialId) runSearch(initialId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-[80vh] bg-gray-50 py-16 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="font-label text-xs text-[#2E7BC7]">Live Tracking</div>
        <h1 className="mt-2 font-display font-black text-3xl md:text-5xl text-[#0B3D6E]">Track your shipment.</h1>
        <p className="mt-3 text-slate-600">Enter any Apex shipment ID (try <span className="font-mono">APX-DEMO001</span> to see a sample).</p>

        <form onSubmit={search} className="mt-8 flex gap-2" data-testid="tracking-form">
          <input value={id} onChange={(e) => setId(e.target.value)} placeholder="APX-XXXXXXXX" required
            className="flex-1 bg-white border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-[#2E7BC7] focus:border-transparent outline-none"
            data-testid="tracking-input" />
          <button disabled={loading} className="btn-primary-apex inline-flex items-center gap-2" data-testid="tracking-submit-btn">
            <Search className="h-4 w-4" /> {loading ? "Searching..." : "Track"}
          </button>
        </form>

        {err && <div className="mt-6 text-red-600 text-sm">{err}</div>}

        {data && (
          <div className="mt-10 bg-white border border-gray-200 rounded-lg p-8" data-testid="tracking-result">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="font-label text-xs text-slate-500">Shipment</div>
                <div className="font-mono font-bold text-lg text-[#0B3D6E]">{data.shipment_id}</div>
              </div>
              <span className="pill bg-orange-100 text-orange-800">{data.status}</span>
            </div>
            <div className="mt-6 grid md:grid-cols-3 gap-6 text-sm">
              <Info label="Origin" value={data.origin} />
              <Info label="Destination" value={data.destination} />
              <Info label={data.current_location ? "Current Location" : "Equipment"} value={data.current_location || data.equipment_type} />
            </div>

            <div className="mt-8">
              <div className="font-label text-xs text-slate-500 mb-4">Tracking events</div>
              <div className="relative pl-6">
                <div className="absolute left-2 top-2 bottom-2 w-px bg-gray-200" />
                {(data.tracking_events || []).map((e, i) => (
                  <div key={`${e.status}-${e.timestamp}-${i}`} className="relative mb-6" data-testid={`event-${i}`}>
                    <div className="absolute -left-4 top-1 h-3 w-3 rounded-full bg-[#2E7BC7] ring-4 ring-white" />
                    <div className="font-semibold text-[#0B3D6E] flex items-center gap-2">
                      {statusIcon(e.status)} {e.status}
                    </div>
                    <div className="text-sm text-slate-600">{e.location}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{new Date(e.timestamp).toLocaleString()}</div>
                    {e.note && <div className="text-xs text-slate-500 italic">{e.note}</div>}
                  </div>
                ))}
              </div>
            </div>

            {data.eta && (
              <div className="mt-4 rounded-md bg-gray-50 border border-gray-200 p-4 flex items-center gap-3">
                <Clock className="h-5 w-5 text-[#2E7BC7]" />
                <div className="text-sm text-slate-700">ETA: <span className="font-semibold">{new Date(data.eta).toLocaleString()}</span></div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="font-label text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-slate-800 font-medium flex items-center gap-1"><MapPin className="h-4 w-4 text-slate-400" /> {value}</div>
    </div>
  );
}
function statusIcon(s) {
  if (s.toLowerCase().includes("delivered")) return <PackageCheck className="h-4 w-4 text-[#2E7BC7]" />;
  if (s.toLowerCase().includes("transit")) return <Truck className="h-4 w-4 text-[#2E7BC7]" />;
  return <MapPin className="h-4 w-4 text-[#2E7BC7]" />;
}
